import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Printer, Copy, FileDown } from "lucide-react";

export default function StrawberryBrief() {
  const { lang } = useT();
  const [d, setD] = useState(null);
  const [view, setView] = useState(lang === "pl" ? "pl" : "en"); // pl | en | both

  useEffect(() => { api.get("/public/strawberry-brief").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-mutedtext">…</div>;

  const p = d.pilot || {};
  const L = (pl, en) => (view === "pl" ? pl : view === "en" ? en : `${pl}\n${en}`);
  const kg = (n) => `${(n || 0).toLocaleString()} kg`;

  const sections = [
    ["1. Executive Summary", `${p.name_pl} / ${p.name_en}. ${(d.investor_sections[0]?.body) || ""}`],
    ["2. Żywiąca Polska mission", "Rolnicy żywią Polskę. Polska chroni rolników. Farmers feed Poland. Poland protects its farmers."],
    ["3. Zero-Rot Principle", "No safe Polish-grown fruit or vegetable should rot because there was no organized, economically viable route to market."],
    ["4. Pilot region & crop", `${p.crop} · ${p.region} · ${p.harvest_period}. District & participating farms to be confirmed.`],
    ["5. Grower-recruitment target", `${p.growers} demonstration growers`],
    ["6. Forecast crop registered", kg(p.forecast_kg)],
    ["7. Fresh institutional demand", kg(p.fresh_institutional_kg)],
    ["8. School & childcare allocation", kg(p.school_kg)],
    ["9. Hospital & senior-care allocation", kg(p.hospital_senior_kg)],
    ["10. Workplace allocation", kg(p.workplace_kg)],
    ["11. Other institutional allocation", kg(p.other_institutional_kg)],
    ["12. Retail & direct demand", kg(p.retail_direct_kg)],
    ["13. Freezing capacity", kg(p.freezing_kg)],
    ["14. Preserve / purée / juice capacity", kg(p.preserve_puree_juice_kg)],
    ["15. Donation contingency", kg(p.donation_contingency_kg)],
    ["16. Initially unassigned quantity", kg(p.unassigned_kg)],
    ["17. Minimum viable harvest price (fictional)", `${p.min_viable_price} PLN/kg`],
    ["18. Current illustrative market offer", `${p.market_offer} PLN/kg`],
    ["19. Estimated funding gap (live)", `${d.live.funding_gap_pln_estimate?.toLocaleString()} PLN — ${d.live.label}`],
    ["20. Cold-chain & daily collection", `Refrigerated collection: ${p.refrigerated_collection ? "required" : "no"}; Daily collection: ${p.daily_collection ? "required" : "no"}.`],
    ["21. Product destinations", (d.destinations || []).join(" → ")],
    ["22. Food-safety & traceability controls", "Human food-safety review; unique traceability IDs per lot & shipment; private data never public. Damaged/unsafe fruit is never distributed to children, patients or seniors."],
    ["23. Farmer settlement structure", "Transparent gross price, quantity accepted, itemised deductions (each with amount, reason, responsible user, timestamp), payable to farmer."],
    ["24. Zero-Rot impact metrics (live/demo)", `Forecast ${kg(d.live.forecast_kg)}; matched ${kg(d.live.matched_kg)}; at risk ${kg(d.live.at_risk_kg)}.`],
    ["25. Risks & mitigations", "Rapid deterioration, refrigeration delay, rain damage, daily picking variation, processing congestion, packaging & labor shortage, delivery cancellation — mitigated via daily cold collection, processing fallback and staggered scheduling."],
    ["26. Post-harvest evaluation", "August–September 2027: settlement, independent evaluation, investor report and next-crop expansion plan."],
    ["27. Expansion & partnership call to action", "Expansion into raspberries, cherries, plums, apples and vegetables. Partners still required: growers, institutions, processors, logistics, cold storage, municipalities, finance."],
  ];

  const buildMarkdown = () => {
    let md = `# ${p.name_pl}\n# ${p.name_en}\n\n_${d.metric_label}_\n_Generated: ${new Date(d.generated_at).toLocaleString()}_\n\n`;
    sections.forEach(([h, b]) => { md += `## ${h}\n${b}\n\n`; });
    md += `## Preparation timeline (2026–2027)\n`;
    (d.timeline || []).forEach((t) => { md += `- **${t.period}** — ${t.stage}\n`; });
    md += `\n_${d.disclaimers?.legal || ""}_\n`;
    return md;
  };
  const copyMd = () => { navigator.clipboard.writeText(buildMarkdown()); toast.success("Markdown copied"); };
  const downloadMd = () => {
    const blob = new Blob([buildMarkdown()], { type: "text/markdown" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "truskawka-2027-brief.md"; a.click();
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex gap-1 rounded-md border border-subtle p-0.5">
          {[["pl", "PL"], ["en", "EN"], ["both", "PL+EN"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`brief-view-${v}`} className={`rounded px-3 py-1 text-xs font-semibold ${view === v ? "bg-forest text-white" : "text-charcoal"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.print()} data-testid="brief-print" className="inline-flex items-center gap-1 rounded-md border border-forest px-3 py-1.5 text-sm font-semibold text-forest hover:bg-forest/5"><Printer className="h-4 w-4" /> Print / PDF</button>
          <button onClick={copyMd} className="inline-flex items-center gap-1 rounded-md border border-subtle px-3 py-1.5 text-sm font-semibold text-charcoal hover:bg-secondary"><Copy className="h-4 w-4" /> Copy MD</button>
          <button onClick={downloadMd} data-testid="brief-md" className="inline-flex items-center gap-1 rounded-md border border-subtle px-3 py-1.5 text-sm font-semibold text-charcoal hover:bg-secondary"><FileDown className="h-4 w-4" /> .md</button>
        </div>
      </div>

      <div className="rounded-md border border-subtle bg-white p-8">
        <div className="mb-1 text-sm font-semibold uppercase tracking-wide text-polishred">Truskawka 2027</div>
        <h1 className="font-heading text-3xl font-bold text-charcoal">{L(p.name_pl, p.name_en)}</h1>
        <p className="mt-2 inline-block rounded bg-warnbg px-2 py-0.5 text-xs font-semibold text-warntext">{p.disclaimer_pl} / {p.disclaimer_en}</p>
        <p className="mt-1 text-xs text-mutedtext">Generated: {new Date(d.generated_at).toLocaleString()} · {d.metric_label}</p>

        <div className="mt-8 space-y-6">
          {sections.map(([h, b]) => (
            <div key={h} data-testid={`brief-sec-${h.split(".")[0]}`}>
              <h2 className="font-heading text-lg font-bold text-charcoal">{h}</h2>
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-mutedtext">{b}</p>
            </div>
          ))}
          <div>
            <h2 className="font-heading text-lg font-bold text-charcoal">Preparation timeline (2026–2027)</h2>
            <ul className="mt-2 space-y-1.5">
              {(d.timeline || []).map((t) => (
                <li key={t.period} className="flex gap-3 text-sm"><span className="w-40 shrink-0 font-semibold text-forest">{t.period}</span><span className="text-mutedtext">{t.stage}</span></li>
              ))}
            </ul>
          </div>
          <p className="rounded-md border border-forest/20 bg-forest/5 p-4 text-xs text-charcoal">{d.disclaimers?.legal}</p>
        </div>
      </div>
    </div>
  );
}
